import React, { useMemo } from 'react';
import { Checkbox, Tooltip } from '@mui/material';
import { PERMISSION_LABEL_FALLBACKS } from '../../constants/permissions';
import styles from './PlatformAdmin.module.scss';

/**
 * Grouped permission-key checkbox list (spec §4.6).
 *
 * Renders the catalog grouped by `category`, with the label shown and the
 * description as a tooltip. Always render from a live catalog so new areas appear
 * automatically — never hardcode the keys.
 *
 * Props:
 *   - catalog: [{ key, label, description, scope, category }]
 *   - selected: string[]              currently-checked keys
 *   - onToggle: (key) => void
 *   - disabledKeys?: string[]         keys shown but not toggleable
 *   - lockedKeys?: string[]           shown CHECKED and not toggleable (e.g. keys
 *                                     partner admins always keep)
 *   - keyTooltips?: { [key]: string } replaces the description tooltip (why a key
 *                                     is locked/disabled)
 *   - keyWarnings?: { [key]: string } warning line under the item (opt-in keys)
 *   - readOnly?: boolean              render checkboxes disabled
 */
const EMPTY = [];
const EMPTY_MAP = {};

const PermissionCheckboxGroup = ({
  catalog = EMPTY,
  selected = EMPTY,
  onToggle,
  disabledKeys = EMPTY,
  lockedKeys = EMPTY,
  keyTooltips = EMPTY_MAP,
  keyWarnings = EMPTY_MAP,
  readOnly = false,
}) => {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const disabledSet = useMemo(() => new Set(disabledKeys), [disabledKeys]);
  const lockedSet = useMemo(() => new Set(lockedKeys), [lockedKeys]);

  const grouped = useMemo(() => {
    const map = new Map();
    catalog.forEach((perm) => {
      const category = perm.category || 'Other';
      if (!map.has(category)) map.set(category, []);
      map.get(category).push(perm);
    });
    return Array.from(map.entries());
  }, [catalog]);

  if (catalog.length === 0) {
    return <div className={styles.muted}>No permission areas available.</div>;
  }

  return (
    <div className={styles.permGroups}>
      {grouped.map(([category, perms]) => (
        <div key={category} className={styles.permGroup}>
          <div className={styles.permGroupHead}>
            <span className={styles.permGroupTitle}>{category}</span>
          </div>
          <div className={styles.permGrid}>
            {perms.map((perm) => {
              const locked = lockedSet.has(perm.key);
              const checked = locked || selectedSet.has(perm.key);
              const disabled = readOnly || locked || disabledSet.has(perm.key);
              const warning = keyWarnings[perm.key];
              return (
                <Tooltip
                  key={perm.key}
                  title={keyTooltips[perm.key] || perm.description || ''}
                  placement="top"
                  arrow
                >
                  <label
                    className={`${styles.permItem} ${checked ? styles.checked : ''} ${
                      disabled ? styles.disabled : ''
                    }`}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => onToggle?.(perm.key)}
                      sx={{ p: 0, mt: '2px' }}
                    />
                    <span className={styles.permItemText}>
                      <span className={styles.permItemLabel}>{perm.label || PERMISSION_LABEL_FALLBACKS[perm.key] || perm.key}</span>
                      <span className={styles.permItemKey}>{perm.key}</span>
                      {warning && <span className={styles.permItemWarn}>{warning}</span>}
                    </span>
                  </label>
                </Tooltip>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PermissionCheckboxGroup;
